import { useEffect, useState } from 'react';
import { Autoplay, Pagination } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import { getAspectRatioForHeroBannerImages } from '@/utils/aspectRatioUtils';

import { CMSSection } from '@/cms/CMSSection';
import type {
  ContentfulCardFragment,
  ContentfulHeroSliderFragment,
} from '@/graphql/contentful/api';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import type { CmsExtraDataRecord } from '@/integrations/contentful';
import type { ContentType } from '@/integrations/contentful/types';
import { EVENT_NAME } from '@/lib/gtm/const';
import { generateDataTestId } from '@/lib/tests';

import type { CardProps } from '../Card';
import { Card } from '../Card';

import * as S from './HeroSlider.styles';

type HeroSliderProps = ContentfulHeroSliderFragment & {
  extraDataRecord?: CmsExtraDataRecord;
};

const defaultAspectRatio = {
  x: 0,
  y: 0,
};

export const HeroSlider = ({
  autoslide,
  autoslideDurationMs,
  extraDataRecord,
  slidesCollection,
}: HeroSliderProps) => {
  const { deviceType } = useDeviceDetect();
  const aspectRatio = getAspectRatioForHeroBannerImages(deviceType);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const { sendPromotionEvent } = useGoogleAnalytics();

  useEffect(() => {
    const slide = slidesCollection?.items.filter(Boolean)[activeSlideIndex];
    sendPromotionEvent({
      event: EVENT_NAME.VIEW_PROMOTION,
      creativeName: slide?.internalName as string,
      creativeSlot: activeSlideIndex + 1,
      sku: slide?.saleorProductSku ?? undefined,
    });
  }, [activeSlideIndex]);

  const autoSlideOption = {
    delay: autoslideDurationMs ?? 3000,
    disableOnInteraction: false,
  };

  return (
    <CMSSection
      {...generateDataTestId('heroSlider', 'section')}
      data-testid='HeroSliderSection'
    >
      <S.SwiperWrapper
        $desktopAspectRatio={
          getAspectRatioForHeroBannerImages('desktop') ?? defaultAspectRatio
        }
        $mobileAspectRatio={
          getAspectRatioForHeroBannerImages('mobile') ?? defaultAspectRatio
        }
        autoplay={autoslide ? autoSlideOption : false}
        data-testid='HeroSliderSwiperWrapper'
        modules={[Autoplay, Pagination]}
        onActiveIndexChange={(swiper) => {
          if (!swiper.autoplay.running || swiper.autoplay.timeLeft > 50) {
            setActiveSlideIndex(swiper.activeIndex);
          }
        }}
        pagination={{
          clickable: true,
        }}
      >
        {slidesCollection?.items.filter(Boolean).map((slide, i) => {
          const props = (slide ? { ...slide } : null) as SlideData;
          if (props && i === 0) {
            props.preload = true;
          }

          const renderedSlide = renderSlide(props);

          const extraData =
            extraDataRecord && slide
              ? extraDataRecord[(slide as ContentfulCardFragment)?.sys.id]
              : undefined;

          return (
            renderedSlide && (
              <SwiperSlide
                key={(slide as ContentfulCardFragment)!.sys.id}
                data-testid={`HeroSliderSwiperSlide-${i}`}
              >
                {renderSlide({
                  aspectRatio: aspectRatio,
                  ...props,
                  sizes: '(max-width: 600px) 50vw, 100vw',
                  extraData: extraData?.extraData,
                  heroCardIndex: i,
                } as SlideData)}
              </SwiperSlide>
            )
          );
        })}
      </S.SwiperWrapper>
    </CMSSection>
  );
};

type SlideData = CardProps | null;

export const renderSlide = (data: SlideData) => {
  let props = null;
  const typename = data?.__typename as ContentType;

  if (typename === 'Card') {
    props = data as CardProps;
    return (
      <Card
        {...props}
        data-testid='HeroSliderCard'
        isHeroCard
        loaderProps={{ height: 'fullHeight' }}
        mediaWrapperType='HeroBanner'
        useLoader={true}
      />
    );
  }

  return null;
};

HeroSlider.displayName = 'HeroSlider';
